// src/components/institution/forms/StaffMemberForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, Save, User, Mail, Phone, Briefcase, Shield, GraduationCap, 
  HeadphonesIcon, Plus, Trash2, BookOpen, Users
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { firestoreService } from '../../../services/firestoreService';

// Staff role configurations
const STAFF_ROLES = {
  admin: {
    label: 'Administrator',
    icon: Shield,
    color: '#dc2626',
    description: 'Full system access and management permissions'
  },
  educator: {
    label: 'Educator',
    icon: GraduationCap,
    color: '#059669',
    description: 'Teaching staff with subject expertise'
  },
  support: {
    label: 'Support Staff',
    icon: HeadphonesIcon,
    color: '#7c3aed',
    description: 'Administrative and support functions'
  },

  manager: {
    label: 'School Management Team',
    icon: Briefcase,
    oolor: '#1a5b28',
    description: 'School Management Teach members'
  }
};

// Common subjects for educators
const SUBJECTS = [
  'Mathematics', 'English', 'Science', 'Physics', 'Chemistry', 'Biology',
  'History', 'Geography', 'Computer Science', 'Art', 'Music', 'Physical Education',
  'Languages', 'Literature', 'Economics', 'Business Studies', 'Psychology',
  'Philosophy', 'Sociology', 'Engineering', 'Technology', 'Health Sciences'
];

// Grade levels
const GRADE_LEVELS = [
  'Grade R', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
  'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12',
  'Foundation', 'Intermediate', 'Senior', 'University Level'
];

export const StaffMemberForm = ({ staffMember, onSave, onClose, bookId }) => {
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    department: '',
    email: '',
    phone: '',
    bio: '',
    qualifications: '',
    profileImage: '',
    startDate: '',
    officeLocation: '',
    officeHours: '',
    isPublic: true,
    role: 'support', // admin, educator, support. manager
    subjects: [], // For educators
    grades: [], // For educators
    specializations: []
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [errors, setErrors] = useState({});
  const [newSubject, setNewSubject] = useState('');
  const [newGrade, setNewGrade] = useState('');

  useEffect(() => {
    if (staffMember) {
      setFormData({
        ...staffMember,
        subjects: staffMember.subjects || [],
        grades: staffMember.grades || []
      });
    }
  }, [staffMember]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Fixed upload function based on project knowledge
  const handleImageUpload = async (file, error) => {
    if (error) {
      setUploadError(error);
      return;
    }

    if (!file || !bookId) {
      console.log('Missing file or bookId:', { file: !!file, bookId });
      return;
    }

    setUploading(true);
    setUploadError('');
    console.log('Starting upload for:', file.name);

    try {
      // Use the correct method signature and path format
      const result = await firestoreService.uploadFile(file, `pb-${bookId}/staff`, {
        maxSize: 5 * 1024 * 1024, // 5MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      });
      
      console.log('Upload result:', result);
      
      // The method returns an object with url property
      setFormData(prev => ({ ...prev, profileImage: result.url }));
      
    } catch (error) {
      console.error('Error uploading profile image:', error);
      setUploadError(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, profileImage: '' }));
    setUploadError('');
  };

  const addSubject = () => {
    if (newSubject && !formData.subjects.includes(newSubject)) {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, newSubject]
      }));
      setNewSubject('');
    }
  };

  const removeSubject = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(s => s !== subject)
    }));
  };

  const addGrade = () => {
    if (newGrade && !formData.grades.includes(newGrade)) {
      setFormData(prev => ({
        ...prev,
        grades: [...prev.grades, newGrade]
      }));
      setNewGrade('');
    }
  };

  const removeGrade = (grade) => {
    setFormData(prev => ({
      ...prev,
      grades: prev.grades.filter(g => g !== grade)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.position.trim()) {
      newErrors.position = 'Position is required';
    }
    if (!formData.department.trim()) {
      newErrors.department = 'Department is required';
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    
    // Additional validation for educators
    if (formData.role === 'educator') {
      if (formData.subjects.length === 0) {
        newErrors.subjects = 'At least one subject is required for educators';
      }
      if (formData.grades.length === 0) {
        newErrors.grades = 'At least one grade level is required for educators';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await onSave({
        ...formData,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving staff member:', error);
    }
  };

  const selectedRole = STAFF_ROLES[formData.role];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <User className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {staffMember ? 'Edit Staff Member' : 'Add New Staff Member'}
              </h2>
              <p className="text-sm text-gray-600">Staff profile information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Profile Image */}
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <FileUpload
                  onFileSelect={handleImageUpload}
                  currentFile={formData.profileImage}
                  onRemove={handleRemoveImage}
                  accept="image/*"
                  maxSize={5}
                  label={formData.profileImage ? "Change Photo" : "Upload Photo"}
                  uploading={uploading}
                  error={uploadError}
                  showPreview={true}
                  aspectRatio="1:1"
                  className="w-24 h-24"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Profile Photo</h3>
                <p className="text-sm text-gray-600">
                  Upload a professional profile photo. Square format recommended (400x400px).
                </p>
                {uploadError && (
                  <p className="text-red-600 text-sm mt-2">{uploadError}</p>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Full name"
                />
                {errors.name && (
                  <p className="text-red-600 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position/Title *
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.position ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Job title/position"
                />
                {errors.position && (
                  <p className="text-red-600 text-xs mt-1">{errors.position}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department *
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.department ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Department name"
                />
                {errors.department && (
                  <p className="text-red-600 text-xs mt-1">{errors.department}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {Object.entries(STAFF_ROLES).map(([key, role]) => (
                    <option key={key} value={key}>
                      {role.label}
                    </option>
                  ))}
                </select>
                
                {/* Role description */}
                <div className="mt-2 p-3 rounded-lg" style={{ backgroundColor: `${selectedRole.color}10` }}>
                  <div className="flex items-center gap-2 text-sm">
                    <selectedRole.icon className="h-4 w-4" style={{ color: selectedRole.color }} />
                    <span className="font-medium" style={{ color: selectedRole.color }}>
                      {selectedRole.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{selectedRole.description}</p>
                </div>
              </div>
            </div>

            {/* Educator-specific fields */}
            {formData.role === 'educator' && (
              <div className="space-y-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-4">
                  <GraduationCap className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-medium text-green-900">Educator Information</h3>
                </div>

                {/* Subjects */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subjects Taught *
                  </label>
                  
                  {/* Add new subject */}
                  <div className="flex gap-2 mb-3">
                    <select
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select a subject</option>
                      {SUBJECTS.filter(s => !formData.subjects.includes(s)).map(subject => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={addSubject}
                      disabled={!newSubject}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </button>
                  </div>

                  {/* Selected subjects */}
                  <div className="flex flex-wrap gap-2">
                    {formData.subjects.map(subject => (
                      <span
                        key={subject}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                      >
                        <BookOpen className="h-3 w-3" />
                        {subject}
                        <button
                          type="button"
                          onClick={() => removeSubject(subject)}
                          className="hover:text-green-900"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  {errors.subjects && (
                    <p className="text-red-600 text-xs mt-1">{errors.subjects}</p>
                  )}
                </div>

                {/* Grades */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grade Levels Taught *
                  </label>
                  
                  {/* Add new grade */}
                  <div className="flex gap-2 mb-3">
                    <select
                      value={newGrade}
                      onChange={(e) => setNewGrade(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select a grade level</option>
                      {GRADE_LEVELS.filter(g => !formData.grades.includes(g)).map(grade => (
                        <option key={grade} value={grade}>{grade}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={addGrade}
                      disabled={!newGrade}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </button>
                  </div>

                  {/* Selected grades */}
                  <div className="flex flex-wrap gap-2">
                    {formData.grades.map(grade => (
                      <span
                        key={grade}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                      >
                        <Users className="h-3 w-3" />
                        {grade}
                        <button
                          type="button"
                          onClick={() => removeGrade(grade)}
                          className="hover:text-blue-900"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  {errors.grades && (
                    <p className="text-red-600 text-xs mt-1">{errors.grades}</p>
                  )}
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Email address"
                />
                {errors.email && (
                  <p className="text-red-600 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Phone number"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Biography
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Brief biography and background"
              />
            </div>

            {/* Professional Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Qualifications
                </label>
                <textarea
                  value={formData.qualifications}
                  onChange={(e) => handleInputChange('qualifications', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Education, degrees, certifications"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Office Location
                </label>
                <input
                  type="text"
                  value={formData.officeLocation}
                  onChange={(e) => handleInputChange('officeLocation', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Building, room number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Office Hours
                </label>
                <input
                  type="text"
                  value={formData.officeHours}
                  onChange={(e) => handleInputChange('officeHours', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Mon-Fri 9:00-17:00"
                />
              </div>
            </div>

            {/* Visibility */}
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                  className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Show in public directory</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              <Save className="h-4 w-4" />
              {uploading ? 'Saving...' : 'Save Staff Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};